<template>
	<button
		class="kova-v2-btn"
		:class="[`kova-v2-btn_${variant}`, { [`kova-v2-btn_tone_${tone}`]: variant === 'floating' }]"
		type="button"
		@click="$emit('click')"
	>
		<span class="kova-v2-btn__label">{{ label }}</span>
		<InlineSvg v-if="variant !== 'header'" aria-hidden="true" class="kova-v2-btn__arrow" :src="arrow" />
	</button>
</template>

<script setup lang="ts">
import arrow from '@/assets/icons/ds_arrow_right.svg'

// Internal to the v2 sections — not a schema block, so not in the registry. It only says
// "clicked"; the owning section emits `progressLoaderCompleted` with its entry point and
// LandingView owns tracking and navigation.
defineOptions({ name: 'LandingKovaV2Button' })

withDefaults(
	defineProps<{
		label: string
		/**
		 * hero     — mobile lime 350x48 (6357:60809), desktop green 397x90 (6547:16860)
		 * section  — the 397x90 green desktop CTA (6547:17172, 6547:17231, 6566:33085)
		 * header   — desktop header pill without arrow (6547:16828)
		 * floating — the fixed action, 358x52 / radius 16 / Poppins Medium 16 (6547:4967)
		 */
		variant: 'header' | 'hero' | 'section' | 'floating'
		/** Floating variant only: `body` is the green 6547:4967, `hero` keeps the lime first-screen colours. */
		tone?: 'hero' | 'body'
	}>(),
	{ tone: 'body' },
)

defineEmits<{ click: [] }>()
</script>

<style lang="scss" scoped>
.kova-v2-btn {
	box-sizing: border-box;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: var(--size-8);
	max-width: 100%;
	margin: 0;

	font-family: var(--kova-lp-font);
	font-weight: 500;
	text-align: center;

	border: none;
	color: var(--Fill-White);
	background: var(--kova-lp-brand);
	cursor: pointer;
}

.kova-v2-btn__arrow {
	flex-shrink: 0;
}

// Mobile hero (6357:60809): lime, Inter SemiBold 16/18, 20px arrow, 4px gap.
.kova-v2-btn_hero {
	width: 100%;
	min-height: var(--size-48);
	gap: var(--size-4);
	padding: var(--size-12);

	font-family: var(--font-family);
	font-size: var(--size-16);
	font-weight: 600;
	line-height: var(--size-18);

	border-radius: var(--size-16);
	color: var(--kova-lp-hero-cta-ink);
	background: var(--kova-lp-hero-accent);

	.kova-v2-btn__arrow {
		width: var(--size-20);
		height: var(--size-20);
	}

	// The ring sits on the green panel — lime keeps it visible there.
	&:focus-visible {
		outline-color: var(--kova-lp-hero-accent);
	}
}

// 6547:4967 — the floating geometry: 52 high (never below 44px, whatever the root font
// size — the rem ladder shrinks under 376px), Poppins Medium 16/20, 16px radius. The
// `section` variant is desktop-only (the mobile frames draw none) and is styled below.
.kova-v2-btn_floating {
	min-height: max(44px, var(--size-52));
	padding: var(--size-16) var(--size-32);

	font-size: var(--size-16);
	line-height: var(--size-20);

	border-radius: var(--size-16);
	white-space: normal;

	.kova-v2-btn__arrow {
		width: var(--size-20);
		height: var(--size-20);
	}

	// Both tones sit on the cream page; the focus ring stays dark green.
	&:focus-visible {
		outline-color: var(--kova-lp-brand);
	}
}

.kova-v2-btn_tone_hero {
	color: var(--kova-lp-hero-cta-ink);
	background: var(--kova-lp-hero-accent);
}

// Header pill (6547:16828): 42/20 padding, 24 radius, Poppins Medium 20, no arrow.
.kova-v2-btn_header {
	padding: var(--size-20) var(--size-42);

	font-size: var(--size-20);
	line-height: var(--size-30);
	white-space: nowrap;

	border-radius: var(--size-24);
}

@media (min-width: 1024px) {
	// 397x90 (6547:16860): 60px side padding hugging the label, 28 radius, 24px label,
	// 32px arrow, 8px gap. Same for the hero and the section CTAs.
	.kova-v2-btn_hero,
	.kova-v2-btn_section {
		width: auto;
		min-height: var(--size-88); // Closest to 90px in Figma 6547:16860.
		gap: var(--size-8);
		padding: var(--size-20) var(--size-60);

		font-family: var(--kova-lp-font);
		font-size: var(--size-24);
		font-weight: 500;
		line-height: var(--size-36);

		border-radius: var(--size-28);
		color: var(--Fill-White);
		background: var(--kova-lp-brand);

		.kova-v2-btn__arrow {
			width: var(--size-32);
			height: var(--size-32);
		}

		&:focus-visible {
			outline-color: var(--kova-lp-brand);
		}
	}
}
</style>
